import { Badge, Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Button, Flex, Input, InputGroup, InputLeftElement, Select, SkeletonText, Stack, Text, useColorModeValue, useToast, Table, Thead, Tbody, Tr, Th, Td, TableContainer, HStack, Tooltip, Center, Icon, IconButton } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ReactSelect from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import { FaFilePdf, FaImage } from 'react-icons/fa';
import { SearchIcon } from '@chakra-ui/icons';
import { GrPowerReset } from 'react-icons/gr';
import axiosClient from '../../../axios-client';
import { useStateContext } from '../../../context/ContextProvider';
import MyPagination from '../../../components/MyPagination';

function AllStock() {
    const { user, currentBusiness } = useStateContext()
    const { t } = useTranslation();
    const navigate = useNavigate()
    const toast = useToast()

    const colorbg = useColorModeValue('white', '#2d3748')
    const txtcolor = useColorModeValue('black', 'white')
    const colorbghover = useColorModeValue('#e2e8f0', '#171923')
    const descColor = useColorModeValue('gray.400', 'gray.500')
    const theadBg = useColorModeValue('gray.50', 'gray.800')
    const trHoverBg = useColorModeValue('gray.50', 'gray.700/50')
    const borderColor = useColorModeValue("gray.300", "gray.600")

    const selectTextColor = useColorModeValue('black', 'white');
    const selectBg = useColorModeValue('transparent', 'transparent');
    const selectMenuBg = useColorModeValue('white', '#2D3748');
    const selectBorderColor = useColorModeValue('#E2E8F0', 'rgba(255, 255, 255, 0.16)');
    const selectHoverBg = useColorModeValue('#EDF2F7', '#4A5568');

    const [stockData, setStockData] = useState([])
    const [loading, setLoading] = useState(true)

    // Filters
    const [filterStatus, setFilterStatus] = useState(null)
    const [filterQuery, setFilterQuery] = useState('')
    const [filterDate, setFilterDate] = useState('')
    const [selectedProduct, setSelectedProduct] = useState(null)

    // Pagination
    const [currentPage, setCurrentPage] = useState(0)
    const PAGE_SIZE = 10
    const [pageSize, setPageSize] = useState(PAGE_SIZE)

    const fetchAllStock = async () => {
        if (!currentBusiness?.id) return
        try {
            setLoading(true)
            const res = await axiosClient.get(`/stock/getAllStock/?business=${currentBusiness.id}`)
            setStockData(res.data)
        } catch (err) {
            toast({
                description: t("Erreur lors de la récupération du stock global"),
                status: 'error',
                duration: 2000,
                isClosable: true,
                variant: 'left-accent',
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchAllStock()
    }, [currentBusiness?.id])

    const handlePageSizeChange = (event) => {
        setPageSize(parseInt(event.target.value, 10))
        setCurrentPage(0)
    }

    const resetFilters = () => {
        setFilterStatus(null)
        setFilterQuery('')
        setFilterDate('')
        setSelectedProduct(null)
        setCurrentPage(0)
    }

    const loadProductOptions = async (search, loadedOptions, { page }) => {
        try {
            const res = await axiosClient.get(`/products/get_All_Products_names/?client=${user?.id}&page_size=20&page=${page}&search_term=${search}&userRole=${user?.role}&business=${currentBusiness?.id}`, { hideLoader: true });
            const results = res.data.results || [];
            const hasMore = res.data.next !== null && res.data.next !== undefined;

            return {
                options: results.map(product => ({
                    value: product.id,
                    label: product.title
                })),
                hasMore: hasMore,
                additional: {
                    page: page + 1,
                },
            };
        } catch (error) {
            return { options: [], hasMore: false };
        }
    };

    // Filter logic
    const filteredStock = stockData.filter(item => {
        if (filterStatus) {
            if ((item.status || 'Disponible') !== filterStatus.value) return false
        }
        if (filterDate) {
            const itemDate = item.purchase_date || item.date_creation?.split('T')[0]
            if (itemDate !== filterDate) return false
        }
        if (selectedProduct) {
            if (item.product !== selectedProduct.value) return false
        }
        if (filterQuery.trim() !== '') {
            const query = filterQuery.toLowerCase()
            const productTitle = (item.product_title || '').toLowerCase()
            const productCode = (item.product_code || '').toLowerCase()
            const color = (item.color || '').toLowerCase()
            const size = (item.size || '').toLowerCase()
            const quantity = String(item.quantity || '').toLowerCase()
            const price = String(item.purchase_price || '').toLowerCase()
            const weight = String(item.stock_weight || '').toLowerCase()
            const source = (item.source || '').toLowerCase()
            const warehouse = (item.warehouse || '').toLowerCase()

            const match = productTitle.includes(query) ||
                          productCode.includes(query) ||
                          color.includes(query) ||
                          size.includes(query) ||
                          quantity.includes(query) ||
                          price.includes(query) ||
                          weight.includes(query) ||
                          source.includes(query) ||
                          warehouse.includes(query)
            if (!match) return false
        }
        return true
    })

    // Paginated filtered stock
    const paginatedStock = filteredStock.slice(currentPage * pageSize, (currentPage + 1) * pageSize)
    const totalPages = Math.ceil(filteredStock.length / pageSize)

    // Pagination payload expected by MyPagination
    const paginationData = {
        count: filteredStock.length,
        next: currentPage < totalPages - 1 ? 'next' : null,
        previous: currentPage > 0 ? 'prev' : null,
        results: paginatedStock
    }

    return (
        <Stack position={'relative'} spacing={6} px={4} py={3}>
            {/* Header section */}
            <Flex justify="space-between" align="center" direction={{ base: 'column', md: 'row' }} gap={4}>
                <Box pt='12px'>
                    <Breadcrumb fontFamily='Plus Jakarta Display' fontStyle={"italic"} fontSize={{ base: "20px", sm: "25px", lg: "27px" }} color="gray">
                        <BreadcrumbItem>
                            <BreadcrumbLink onClick={() => navigate('/')}>{t("Accueil")}</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem>
                            <BreadcrumbLink onClick={() => navigate('/My_Stock')}>{t("Gestion stock")}</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem isCurrentPage>
                            <BreadcrumbLink>{t("Stock Global")}</BreadcrumbLink>
                        </BreadcrumbItem>
                    </Breadcrumb>
                </Box>
            </Flex>

            {/* Same layout as list_new_orders */}
            <Flex
                px={2}
                py={5}
                mx={5}
                bg={useColorModeValue('white', 'gray.700')}
                rounded={"2xl"}
                mt={2}
                align={'center'}
                justify={'center'}
            >
                <Stack w={'full'} maxW='full' rounded={'lg'} mt={2} px={{ base: 1, md: 5 }}>
                    {/* Filters Row */}
                    <Flex align="center" gap={3} w="full" direction={{ base: 'column', lg: 'row' }} justify="space-between" mb={6}>
                        <Flex gap={3} w="full" direction={{ base: 'column', md: 'row' }} flexWrap="wrap">
                            
                            {/* Product Filter React Select */}
                            <Box w={{ base: '100%', md: '250px' }}>
                                <AsyncPaginate
                                    loadOptions={loadProductOptions}
                                    onChange={(option) => {
                                        setCurrentPage(0);
                                        setSelectedProduct(option);
                                    }}
                                    value={selectedProduct}
                                    isClearable
                                    placeholder={t('Filtrer par produit...')}
                                    noOptionsMessage={() => t("Aucun produit trouvé")}
                                    additional={{ page: 1 }}
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            backgroundColor: selectBg,
                                            borderColor: selectBorderColor,
                                            color: selectTextColor,
                                            minHeight: '40px',
                                            borderRadius: '12px'
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            zIndex: 9999,
                                            backgroundColor: selectMenuBg,
                                            borderRadius: '12px'
                                        }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? selectHoverBg : 'transparent',
                                            color: selectTextColor
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: selectTextColor
                                        }),
                                        input: (provided) => ({
                                            ...provided,
                                            color: selectTextColor
                                        })
                                    }}
                                />
                            </Box>

                            {/* Status Filter */}
                            <Box w={{ base: '100%', md: '200px' }}>
                                <ReactSelect
                                    isClearable
                                    options={[
                                        { value: 'Disponible', label: t('Disponible') },
                                        { value: 'Épuisé', label: t('Épuisé') },
                                        { value: 'En transit', label: t('En transit') },
                                        { value: 'Endommagé', label: t('Endommagé') }
                                    ]}
                                    value={filterStatus}
                                    onChange={(val) => {
                                        setCurrentPage(0)
                                        setFilterStatus(val)
                                    }}
                                    placeholder={t('Filtrer par statut')}
                                    styles={{
                                        control: (baseStyles) => ({
                                            ...baseStyles,
                                            backgroundColor: selectBg,
                                            borderColor: selectBorderColor,
                                            color: selectTextColor,
                                            minHeight: '40px',
                                            borderRadius: '12px'
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            textAlign: 'left',
                                            backgroundColor: colorbg,
                                            cursor: "pointer",
                                            zIndex: 9999,
                                            borderRadius: '12px'
                                        }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            color: txtcolor,
                                            backgroundColor: state.isSelected ? colorbg : 'transparent',
                                            '&:hover': {
                                                backgroundColor: colorbghover,
                                            },
                                            cursor: "pointer"
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: txtcolor,
                                        })
                                    }}
                                />
                            </Box>

                            {/* Date Filter */}
                            <Box w={{ base: '100%', md: '180px' }}>
                                <Input
                                    type="date"
                                    value={filterDate}
                                    onChange={(e) => {
                                        setCurrentPage(0)
                                        setFilterDate(e.target.value)
                                    }}
                                    rounded="xl"
                                    borderColor={borderColor}
                                    size="md"
                                    h="40px"
                                />
                            </Box>

                            {/* Text Search */}
                            <InputGroup size="md" w={{ base: '100%', md: '250px' }}>
                                <InputLeftElement pointerEvents='none' h="40px">
                                    <SearchIcon color="gray.400" />
                                </InputLeftElement>
                                <Input
                                    borderColor={borderColor}
                                    borderRadius="xl"
                                    h="40px"
                                    placeholder={t("Recherche...")}
                                    value={filterQuery}
                                    onChange={(e) => {
                                        setCurrentPage(0)
                                        setFilterQuery(e.target.value)
                                    }}
                                    _focus={{ borderColor: 'purple.500', boxShadow: 'none' }}
                                />
                            </InputGroup>

                            {/* Reset Button */}
                            <IconButton
                                icon={<GrPowerReset />}
                                onClick={resetFilters}
                                variant="outline"
                                colorScheme="purple"
                                rounded="xl"
                                h="40px"
                                w="40px"
                                aria-label={t("Réinitialiser")}
                                title={t("Réinitialiser les filtres")}
                            />
                        </Flex>
                    </Flex>

                    {/* Table Section */}
                    {loading ? (
                        <TableContainer>
                            <Table variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th><SkeletonText noOfLines={1} /></Th>
                                        <Th><SkeletonText noOfLines={1} /></Th>
                                        <Th><SkeletonText noOfLines={1} /></Th>
                                        <Th><SkeletonText noOfLines={1} /></Th>
                                        <Th><SkeletonText noOfLines={1} /></Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    <Tr>
                                        <Td><SkeletonText noOfLines={1} /></Td>
                                        <Td><SkeletonText noOfLines={1} /></Td>
                                        <Td><SkeletonText noOfLines={1} /></Td>
                                        <Td><SkeletonText noOfLines={1} /></Td>
                                        <Td><SkeletonText noOfLines={1} /></Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </TableContainer>
                    ) : filteredStock.length > 0 ? (
                        <TableContainer>
                            <Table variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th borderBottomColor='gray.400'>{t("Produit")}</Th>
                                        <Th borderBottomColor='gray.400'>{t("Code")}</Th>
                                        <Th borderBottomColor='gray.400'>{t("Couleur")}</Th>
                                        <Th borderBottomColor='gray.400'>{t("Taille")}</Th>
                                        <Th borderBottomColor='gray.400'>{t("Quantité")}</Th>
                                        <Th borderBottomColor='gray.400'>{t("Statut")}</Th>
                                        <Th borderBottomColor='gray.400'>{t("Prix d'achat")}</Th>
                                        <Th borderBottomColor='gray.400'>{t("Poids")}</Th>
                                        <Th borderBottomColor='gray.400'>{t("Source")}</Th>
                                        <Th borderBottomColor='gray.400'>{t("Entrepôt / Service")}</Th>
                                        <Th borderBottomColor='gray.400'>{t("Reçu")}</Th>
                                        <Th borderBottomColor='gray.400'>{t("Date d'achat")}</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {paginatedStock.map((val, key) => (
                                        <Tr key={key}>
                                            <Td fontWeight="semibold" color="purple.500">{val.product_title}</Td>
                                            <Td>
                                                <Badge px={2} py={0.5} rounded="md" variant="subtle" colorScheme="gray">
                                                    {val.product_code}
                                                </Badge>
                                            </Td>
                                            <Td>{val.color ? val.color : '-'}</Td>
                                            <Td>{val.size ? val.size : '-'}</Td>
                                            <Td fontWeight="medium">{val.quantity}</Td>
                                            <Td>
                                                <Badge colorScheme={
                                                    val.status === 'Disponible' ? 'green' :
                                                    val.status === 'Épuisé' ? 'red' :
                                                    val.status === 'En transit' ? 'orange' :
                                                    val.status === 'Endommagé' ? 'yellow' : 'gray'
                                                }>
                                                    {t(val.status || 'Disponible')}
                                                </Badge>
                                            </Td>
                                            <Td>
                                                <Flex gap={1} align="center">
                                                    <Text fontWeight="semibold">{val.purchase_price}</Text> 
                                                    <Text fontSize="11px" color="gray.500">{currentBusiness?.currency}</Text>
                                                </Flex>
                                            </Td>
                                            <Td>{val.stock_weight ? `${val.stock_weight} ${val.weight_unit || 'kg'}` : '-'}</Td>
                                            <Td>{val.source ? val.source : '-'}</Td>
                                            <Td>{val.warehouse ? val.warehouse : '-'}</Td>
                                            <Td>
                                                {((val.receipts && val.receipts.length > 0) || val.recu) ? (
                                                    <HStack spacing={1}>
                                                        {[...(val.receipts || []), ...(val.recu ? [{file: val.recu, isLegacy: true}] : [])].map((r, i) => {
                                                            const isPdf = r.file?.toLowerCase().endsWith('.pdf');
                                                            return (
                                                                <Tooltip key={i} label={`${t("Reçu")} #${i + 1}${r.isLegacy ? ' (Ancien)' : ''}`}>
                                                                    <a href={r.file} target="_blank" rel="noreferrer">
                                                                        <Center
                                                                            w="28px"
                                                                            h="28px"
                                                                            bg={isPdf ? "red.50" : "blue.50"}
                                                                            color={isPdf ? "red.500" : "blue.500"}
                                                                            rounded="lg"
                                                                            transition="all 0.2s"
                                                                            _hover={{ transform: 'translateY(-2px)', shadow: 'sm', bg: isPdf ? "red.100" : "blue.100" }}
                                                                            border="1px solid"
                                                                            borderColor={isPdf ? "red.200" : "blue.200"}
                                                                        >
                                                                            <Icon as={isPdf ? FaFilePdf : FaImage} fontSize="12px" />
                                                                        </Center>
                                                                    </a>
                                                                </Tooltip>
                                                            )
                                                        })}
                                                    </HStack>
                                                ) : (
                                                    <Text fontSize="xs" color="gray.400">-</Text>
                                                )}
                                            </Td>
                                            <Td>{val.purchase_date ? new Date(val.purchase_date).toLocaleDateString(t('fr-FR')) : new Date(val.date_creation).toLocaleDateString(t('fr-FR'))}</Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Flex direction="column" alignItems='center' gap={2} py={10}>
                            <Text color='gray.500' fontSize="lg">{t("Aucun stock trouvé.")}</Text>
                        </Flex>
                    )}

                    {/* Pagination Section */}
                    {!loading && filteredStock.length > 0 && (
                        <Flex flexDirection={{ base: 'column', lg: "row" }} justify="space-between" align="center" w="100%" mt={4}>
                            <Box flex="1">
                                <MyPagination
                                    paginationData={paginationData}
                                    currentPage={currentPage}
                                    setCurrentPage={setCurrentPage}
                                    PAGE_SIZE={pageSize}
                                    isLocalPagination={true}
                                />
                            </Box>

                            <Select rounded={"md"} size={"sm"} w="80px" value={pageSize} onChange={handlePageSizeChange}>
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </Select>
                        </Flex>
                    )}
                </Stack>
            </Flex>
        </Stack>
    )
}

export default AllStock
